import { IssueDecisions } from './IssueDecisions';
import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Code,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useRevalidator } from 'react-router-dom';
import { z } from 'zod';
import type {
  ProjectDetail,
  WorkflowException,
} from '@tinypro-audit/contracts';
import { reviewNarrative } from '@tinypro-audit/contracts';
import { formatDateTimeInAppTimeZone } from '@tinypro-audit/shared/time';
import { fetchJson } from '../api/client';
import {
  PreSiteReportLink,
  preSiteReportHref,
} from '../components/PreSiteReportLink';
import { Evidence, PreviewDocument, documentUrl } from './ProjectEvidence';
import { ComparisonChecks } from './WorkflowReviewPanel';
import {
  documentRole,
  fieldLabel,
  openingLabel,
  projectReviewModel,
  readable,
  type ReviewOrder,
} from './project-review-model';

export const reviewDate = (value: string | null | undefined) =>
  value ? formatDateTimeInAppTimeZone(value) : 'Not recorded';
const string = (value: unknown) => (typeof value === 'string' ? value : '');

export function TaskRecords({
  tasks,
  data,
  issueScope,
}: {
  tasks: WorkflowException[];
  data: ProjectDetail;
  issueScope?: { orderId: string; title: string };
}) {
  return <IssueDecisions tasks={tasks} data={data} issueScope={issueScope} />;
}

export function AssessmentList({
  data,
  selectedId,
}: {
  data: ProjectDetail;
  selectedId?: string | null;
}) {
  const [opened, setOpened] = useState<string[]>(
    selectedId ? [selectedId] : [],
  );
  useEffect(() => {
    if (selectedId)
      setOpened((value) =>
        value.includes(selectedId) ? value : [...value, selectedId],
      );
  }, [selectedId]);
  return (
    <Stack gap="sm">
      <div>
        <Title order={3}>Pre-site form reviews</Title>
        <Text size="sm" c="dimmed">
          Original pre-site reports and grades are preserved here. Whether a
          review applies to the current order is shown in the Pre-site review
          card above.
        </Text>
      </div>
      {!data.workflow.assessments.length && (
        <Alert color="yellow">
          No pre-site form review is available in this project’s collected
          evidence.
        </Alert>
      )}
      <Accordion multiple value={opened} onChange={setOpened}>
        {data.workflow.assessments.map((assessment) => (
          <Accordion.Item
            key={assessment.id}
            value={assessment.id}
            id={`assessment-${assessment.id}`}
          >
            <Accordion.Control>
              Review {assessment.source_review_id} · Job{' '}
              {String(assessment.provenance.job_id ?? 'Unknown')} ·{' '}
              {reviewDate(string(assessment.provenance.reviewed_at))} ·{' '}
              {assessment.current
                ? 'Included in latest submission'
                : 'Earlier import'}
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="sm">
                {preSiteReportHref(
                  assessment.provenance.job_id,
                  data.project.st_project_id,
                ) && (
                  <div>
                    <PreSiteReportLink
                      jobId={assessment.provenance.job_id}
                      projectId={data.project.st_project_id}
                    />
                    <Text size="xs" c="dimmed" mt={4}>
                      Opens this job’s latest stored form, estimates and
                      pre-site report. The review below is preserved as
                      originally recorded.
                    </Text>
                  </div>
                )}
                <Text fw={600}>
                  Original pre-site result:{' '}
                  {typeof assessment.assessment.original_result === 'string'
                    ? assessment.assessment.original_result
                    : JSON.stringify(
                        assessment.assessment.original_result ?? 'Unavailable',
                      )}
                </Text>
                {!!assessment.provenance.project_association && (
                  <Text size="sm" c="dimmed">
                    Linked through an observed ServiceTitan job relationship. The historical source review has no project ID; its original findings and grade are preserved.
                  </Text>
                )}
                <Text size="sm">
                  Original classification:{' '}
                  {String(
                    assessment.assessment.original_classification ??
                      'Unavailable',
                  )}
                </Text>
                <Text style={{ whiteSpace: 'pre-wrap' }}>
                  {String(
                    assessment.assessment.original_response ??
                      'Original narrative unavailable.',
                  )}
                </Text>
                <details>
                  <summary>Reviewed inputs and provenance</summary>
                  <Code block className="project-json">
                    {JSON.stringify(
                      {
                        assessment: assessment.assessment,
                        provenance: assessment.provenance,
                        source_fingerprint: assessment.source_fingerprint,
                        imported_at: assessment.imported_at,
                      },
                      null,
                      2,
                    )}
                  </Code>
                </details>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        ))}
      </Accordion>
    </Stack>
  );
}

export function ProjectDocuments({
  data,
  assessmentId,
}: {
  data: ProjectDetail;
  assessmentId: string | null;
}) {
  const submission = data.submissions.find(
    (s) => s.id === data.project.latest_submission_id,
  );
  return (
    <Stack gap="xl">
      {assessmentId && <AssessmentList data={data} selectedId={assessmentId} />}
      <Stack gap="sm">
        <div>
          <Title order={3}>Documents</Title>
          <Text size="sm" c="dimmed">
            Latest accepted evidence · {reviewDate(submission?.accepted_at)}.
            Original files and page citations remain available.
          </Text>
        </div>
        {!data.documents.length && (
          <Text c="dimmed">
            Documents appear after an evidence upload is accepted.
          </Text>
        )}
        {data.documents.map((doc) => (
          <Paper withBorder p="md" radius="md" key={doc.id}>
            <Stack gap="sm">
              <Group justify="space-between" align="start">
                <div className="project-document-name">
                  <Text fw={600}>
                    {String(
                      doc.metadata.original_filename ?? 'Source document',
                    )}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {fieldLabel(documentRole(doc) || 'document')}
                    {typeof doc.metadata.page_count === 'number'
                      ? ` · ${doc.metadata.page_count} pages`
                      : ''}
                  </Text>
                </div>
                <Group gap="xs">
                  <PreviewDocument document={doc} />
                  <Button
                    component="a"
                    href={documentUrl(doc.id)}
                    size="xs"
                    variant="default"
                  >
                    Download
                  </Button>
                </Group>
              </Group>
              <Text size="sm">
                Transcription:{' '}
                {doc.transcription_state === 'completed'
                  ? 'Complete'
                  : fieldLabel(doc.transcription_state ?? 'pending')}
              </Text>
              {doc.transcription_state === 'partial' && (
                <Alert color="yellow">
                  Some pages could not be transcribed. Inspect the original and
                  the recorded page gaps.
                </Alert>
              )}
              <Accordion>
                <Accordion.Item value="transcript">
                  <Accordion.Control>
                    Transcribed text and page details
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="sm">
                      <Text size="sm" className="project-transcript">
                        {doc.transcription ??
                          'No transcription is available yet.'}
                      </Text>
                      <details>
                        <summary>Extraction details</summary>
                        <Code block className="project-json">
                          {JSON.stringify(
                            {
                              pages: doc.pages,
                              sha256: doc.sha256,
                              model: doc.model,
                              prompt_hash: doc.prompt_hash,
                            },
                            null,
                            2,
                          )}
                        </Code>
                      </details>
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            </Stack>
          </Paper>
        ))}
      </Stack>
      {!!data.workflow.collection_work.length && (
        <Paper withBorder p="md">
          <Title order={3}>Evidence follow-ups</Title>
          <Stack mt="sm">
            {data.workflow.collection_work.map((work) => (
              <div key={work.id}>
                <Text fw={500}>
                  {fieldLabel(work.document_role)}
                  {work.order_ref ? ` · Order ${work.order_ref}` : ''}
                </Text>
                <Text size="sm">{reviewNarrative(work.reason)}</Text>
                <Text size="xs" c="dimmed">
                  Next check: {reviewDate(work.next_check_at)} · Required by:{' '}
                  {reviewDate(work.required_by)}
                </Text>
              </div>
            ))}
          </Stack>
        </Paper>
      )}
      {!!submission?.manifest.gaps.length && (
        <Alert color="yellow" title="Recorded collection gaps">
          <Stack gap="xs">
            {submission.manifest.gaps.map((gap, i) => (
              <Text size="sm" key={i}>
                {reviewNarrative(gap)}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}
      {!assessmentId && <AssessmentList data={data} />}
      <Accordion>
        <Accordion.Item value="coverage">
          <Accordion.Control>
            Collection coverage and reused forms
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {submission?.manifest.coverage.map((item, i) => (
                <div key={i}>
                  <Group gap="xs">
                    <Text fw={500}>{fieldLabel(item.category)}</Text>
                    <Badge variant="light">{readable(item.status)}</Badge>
                  </Group>
                  <Text size="sm">{reviewNarrative(item.reason)}</Text>
                </div>
              ))}
              {data.snapshots.map((snapshot) => (
                <details key={snapshot.id}>
                  <summary>Stored form and provenance · {snapshot.id}</summary>
                  <Code block className="project-json">
                    {JSON.stringify(snapshot, null, 2)}
                  </Code>
                </details>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

export function DetailedChecks({
  orders,
  mode,
}: {
  orders: ReviewOrder[];
  mode: 'all' | 'matched' | 'information';
}) {
  return (
    <Stack>
      {orders.map((order) => {
        const checks = order
          .result!.stages.flatMap((stage) => stage.checks)
          .filter(
            (check) =>
              mode === 'all' ||
              (mode === 'matched'
                ? check.outcome === 'match'
                : check.attention === 'information' &&
                  check.outcome !== 'match'),
          );
        if (!checks.length) return null;
        return (
          <div key={order.id}>
            <Title order={4}>
              PO {order.identity.po_number ?? order.order_ref}
            </Title>
            <ComparisonChecks checks={checks} expanded={false} />
          </div>
        );
      })}
    </Stack>
  );
}

export function ProjectHistory({ data }: { data: ProjectDetail }) {
  return (
    <Stack gap="xl">
      <div>
        <Title order={3}>Review and action history</Title>
        <Text size="sm" c="dimmed">
          Previous results and human actions remain available separately from
          the current review.
        </Text>
      </div>
      <Accordion multiple>
        <Accordion.Item value="actions">
          <Accordion.Control>
            Human actions and review events · {data.workflow.events.length}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {data.workflow.events.map((event) => (
                <div className="project-history-event" key={event.id}>
                  <Text fw={500}>{fieldLabel(event.action)}</Text>
                  <Text size="sm" c="dimmed">
                    {reviewDate(event.created_at)} ·{' '}
                    {data.workflow.members.find(
                      (m) => m.user_id === event.actor_user_id,
                    )?.name ??
                      (event.actor_user_id
                        ? 'Reviewer'
                        : 'Analysis / collection')}
                  </Text>
                  {typeof event.details.note === 'string' && (
                    <Text size="sm">{event.details.note}</Text>
                  )}
                  <details>
                    <summary>Supporting records</summary>
                    <Code block className="project-json">
                      {JSON.stringify(event.details, null, 2)}
                    </Code>
                  </details>
                </div>
              ))}
              {!data.workflow.events.length && (
                <Text>No actions recorded.</Text>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value="tasks">
          <Accordion.Control>
            All task records · {data.workflow.exceptions.length}
          </Accordion.Control>
          <Accordion.Panel>
            <TaskRecords tasks={data.workflow.exceptions} data={data} />
          </Accordion.Panel>
        </Accordion.Item>
        <Accordion.Item value="runs">
          <Accordion.Control>
            Analysis runs and submissions · {data.runs.length}
          </Accordion.Control>
          <Accordion.Panel>
            <Stack>
              {data.runs.map((run) => (
                <Paper withBorder p="md" key={run.id}>
                  <Stack gap="sm">
                    <Group>
                      <Badge
                        color={
                          run.state === 'failed'
                            ? 'red'
                            : run.state === 'succeeded'
                              ? 'green'
                              : 'blue'
                        }
                      >
                        {readable(run.state)}
                      </Badge>
                      <Text size="sm">
                        {reviewDate(run.completed_at ?? run.created_at)} ·
                        Attempt {run.attempts}
                      </Text>
                    </Group>
                    {run.result && (
                      <Text size="sm">
                        {reviewNarrative(run.result.summary)}
                      </Text>
                    )}
                    {run.error && (
                      <Alert
                        color="red"
                        title={string(run.error.code) || 'Analysis error'}
                      >
                        {string(run.error.message)}
                      </Alert>
                    )}
                    <details>
                      <summary>Result, identifiers and provenance</summary>
                      <Code block className="project-json">
                        {JSON.stringify(
                          {
                            id: run.id,
                            submission_id: run.submission_id,
                            versions: run.versions,
                            result: run.result,
                            error: run.error,
                          },
                          null,
                          2,
                        )}
                      </Code>
                    </details>
                  </Stack>
                </Paper>
              ))}
              {data.submissions.map((submission) => (
                <details key={submission.id}>
                  <summary>
                    Evidence submission {submission.sequence ?? ''} ·{' '}
                    {reviewDate(
                      submission.accepted_at ?? submission.created_at,
                    )}
                  </summary>
                  <Code block className="project-json">
                    {JSON.stringify(submission, null, 2)}
                  </Code>
                </details>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
        {!!data.reviews.length && (
          <Accordion.Item value="legacy">
            <Accordion.Control>
              Earlier project review actions
            </Accordion.Control>
            <Accordion.Panel>
              <Stack>
                {data.reviews.map((review, i) => (
                  <div key={i}>
                    <Text size="sm">
                      {reviewDate(review.created_at)} ·{' '}
                      {review.resolved ? 'Resolved' : 'Reopened'}
                    </Text>
                    <Text>{review.note}</Text>
                  </div>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>
    </Stack>
  );
}

export function Specifications({
  data,
  runId,
}: {
  data: ProjectDetail;
  runId?: string;
}) {
  const specifications =
    data.runs.find((r) => r.id === runId)?.result?.workflow?.specifications ??
    [];
  return (
    <Table.ScrollContainer minWidth={700}>
      <Table striped>
        <Table.Thead>
          <Table.Tr>
            {['Order / opening', 'Field', 'Value', 'Source'].map((label) => (
              <Table.Th key={label}>{label}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {specifications.map((spec, i) => (
            <Table.Tr key={i}>
              <Table.Td>
                {spec.order_ref}
                <Text size="xs">
                  {spec.opening_ref
                    ? openingLabel(data, spec.opening_ref)
                    : spec.line_ref}
                </Text>
              </Table.Td>
              <Table.Td>{fieldLabel(spec.field)}</Table.Td>
              <Table.Td>
                {spec.raw_value ?? 'Not stated'}
                <Text size="xs" c="dimmed">
                  {spec.normalized_value} {spec.unit}
                </Text>
              </Table.Td>
              <Table.Td>
                <Evidence items={spec.evidence} />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

export function LegacyPurchasingReview({ data }: { data: ProjectDetail }) {
  const run = data.runs.find((r) => r.id === data.project.latest_run_id);
  const [note, setNote] = useState(''),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const revalidator = useRevalidator();
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      await fetchJson(
        `/api/projects/${data.project.st_project_id}/review`,
        {
          method: 'POST',
          body: JSON.stringify({
            run_id: run?.id,
            resolved: !data.project.resolved,
            note,
          }),
        },
        z.unknown(),
      );
      setNote('');
      await revalidator.revalidate();
    } catch (error) {
      setError(
        error instanceof Error ? error.message : 'Review update failed.',
      );
    } finally {
      setBusy(false);
    }
  };
  if (!run?.result) return null;
  return (
    <Stack>
      <Alert color="blue" title="Earlier purchasing review format">
        {reviewNarrative(run.result.summary)}
      </Alert>
      {run.result.findings.map((finding, i) => (
        <Paper withBorder p="md" key={i}>
          <Stack gap="sm">
            <Title order={4}>{fieldLabel(finding.field)}</Title>
            <Text>{reviewNarrative(finding.message)}</Text>
            <Text size="sm">
              Next action: {reviewNarrative(finding.action)}
            </Text>
            <Evidence items={finding.evidence} />
          </Stack>
        </Paper>
      ))}
      <Textarea
        label="Review note"
        value={note}
        onChange={(event) => setNote(event.currentTarget.value)}
        maxLength={4000}
      />
      {error && <Alert color="red">{error}</Alert>}
      <Button
        disabled={!projectReviewModel(data).current || !note.trim()}
        loading={busy}
        onClick={() => void save()}
      >
        {data.project.resolved ? 'Reopen review' : 'Mark resolved'}
      </Button>
    </Stack>
  );
}
